import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Star, Bed, Bath, Users, ChevronLeft, ChevronRight,
  DollarSign, AlertCircle, Loader2, CheckCircle, ZoomIn, X, Zap, Flame
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Airbnb-Style Calendar with price per night
const BookingCalendar = ({ blockedDates = [], onDateSelect, checkIn, checkOut, selectionMode, pricePerNight }) => {
  const [months, setMonths] = useState([]);
  
  useEffect(() => {
    const now = new Date();
    const m = [];
    for (let i = 0; i < 2; i++) {
      m.push(new Date(now.getFullYear(), now.getMonth() + i, 1));
    }
    setMonths(m);
  }, []);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    for (let i = 1; i <= lastDay.getDate(); i++) days.push(new Date(year, month, i));
    return days;
  };

  const isBlocked = (date) => date && blockedDates.includes(date.toISOString().split('T')[0]);
  const isPast = (date) => { if (!date) return false; const t = new Date(); t.setHours(0,0,0,0); return date < t; };
  const isCheckIn = (date) => date && checkIn && date.toISOString().split('T')[0] === checkIn;
  const isCheckOut = (date) => date && checkOut && date.toISOString().split('T')[0] === checkOut;
  const isInRange = (date) => {
    if (!date || !checkIn || !checkOut) return false;
    const d = date.toISOString().split('T')[0];
    return d > checkIn && d < checkOut;
  };
  const isToday = (date) => {
    if (!date) return false;
    const t = new Date(); t.setHours(0,0,0,0);
    return date.getTime() === t.getTime();
  };

  const handleClick = (date) => {
    if (!date || isBlocked(date) || isPast(date)) return;
    onDateSelect(date.toISOString().split('T')[0]);
  };

  const goBack = () => setMonths(m => m.map(d => new Date(d.getFullYear(), d.getMonth() - 1, 1)));
  const goForward = () => setMonths(m => m.map(d => new Date(d.getFullYear(), d.getMonth() + 1, 1)));
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  return (
    <div className="select-none" data-testid="booking-calendar">
      {/* Selection hint */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex gap-2">
          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${selectionMode === 'checkin' ? 'bg-primary text-white border-primary' : 'bg-muted border-border'}`}>
            {checkIn || 'Check-in'}
          </div>
          <span className="text-muted-foreground self-center">→</span>
          <div className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${selectionMode === 'checkout' ? 'bg-primary text-white border-primary' : 'bg-muted border-border'}`}>
            {checkOut || 'Check-out'}
          </div>
        </div>
        {(checkIn || checkOut) && (
          <button type="button" onClick={() => onDateSelect('clear')} className="text-xs text-primary hover:underline">Clear</button>
        )}
      </div>

      {/* Calendar navigation */}
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={goBack} className="p-1.5 hover:bg-muted rounded-full transition-colors"><ChevronLeft size={18} /></button>
        <button type="button" onClick={goForward} className="p-1.5 hover:bg-muted rounded-full transition-colors"><ChevronRight size={18} /></button>
      </div>

      {/* Two-month view on desktop, single on mobile */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {months.map((month, mi) => (
          <div key={mi} className={mi === 1 ? "hidden sm:block" : ""}>
            <h4 className="text-center text-sm font-semibold mb-2">{monthNames[month.getMonth()]} {month.getFullYear()}</h4>
            <div className="grid grid-cols-7 gap-0 text-center">
              {["S","M","T","W","T","F","S"].map((d,i) => (
                <div key={i} className="text-[10px] text-muted-foreground font-medium py-1">{d}</div>
              ))}
              {getDaysInMonth(month).map((date, idx) => {
                const blocked = isBlocked(date);
                const past = isPast(date);
                const ci = isCheckIn(date);
                const co = isCheckOut(date);
                const inR = isInRange(date);
                const today = isToday(date);
                const available = date && !blocked && !past;

                return (
                  <div
                    key={idx}
                    onClick={() => handleClick(date)}
                    className={`
                      relative flex flex-col items-center justify-center py-1 min-h-[42px] sm:min-h-[48px] text-xs transition-all
                      ${!date ? "" :
                        ci ? "bg-primary text-white rounded-l-lg font-bold" :
                        co ? "bg-primary text-white rounded-r-lg font-bold" :
                        inR ? "bg-primary/15" :
                        blocked ? "text-muted-foreground/30 line-through cursor-not-allowed" :
                        past ? "text-muted-foreground/30 cursor-not-allowed" :
                        "hover:bg-primary/10 cursor-pointer"
                      }
                      ${today && !ci && !co ? "font-bold" : ""}
                    `}
                  >
                    {date && (
                      <>
                        <span className={`text-[13px] leading-tight ${ci || co ? 'text-white' : ''}`}>{date.getDate()}</span>
                        {available && !ci && !co && pricePerNight && (
                          <span className="text-[9px] text-muted-foreground/70 leading-tight">${pricePerNight}</span>
                        )}
                        {blocked && !past && (
                          <span className="text-[8px] leading-tight">booked</span>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-primary rounded-sm"></span>Selected</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-primary/15 rounded-sm"></span>Range</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-muted-foreground/20 rounded-sm line-through"></span>Booked</span>
      </div>
    </div>
  );
};

export const BookingModal = ({ villa, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [blockedDates, setBlockedDates] = useState([]);
  const [formData, setFormData] = useState({
    guest_name: "", guest_email: "", guest_phone: "",
    check_in: "", check_out: "", num_guests: 2,
    special_requests: "", coupon_code: "", marketing_opt_in: false,
    include_generator: true, include_bbq: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [numNights, setNumNights] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [dateError, setDateError] = useState("");
  const [couponData, setCouponData] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [selectionMode, setSelectionMode] = useState('checkin');

  useEffect(() => {
    if (villa?.id) {
      axios.get(`${API}/availability/${villa.id}`)
        .then(res => setBlockedDates(res.data.blocked_dates || []))
        .catch(() => setBlockedDates([]));
    }
  }, [villa?.id]);

  const handleDateSelect = (dateStr) => {
    if (dateStr === 'clear') {
      setFormData({...formData, check_in: "", check_out: ""});
      setSelectionMode('checkin');
      return;
    }
    if (selectionMode === 'checkin') {
      setFormData({...formData, check_in: dateStr, check_out: ""});
      setSelectionMode('checkout');
    } else {
      if (dateStr > formData.check_in) {
        setFormData({...formData, check_out: dateStr});
        setSelectionMode('checkin');
      } else {
        setFormData({...formData, check_in: dateStr, check_out: ""});
        setSelectionMode('checkout');
      }
    }
  };

  useEffect(() => {
    setDateError("");
    setCouponData(null);
    setCouponError("");
    if (formData.check_in && formData.check_out && villa) {
      const checkIn = new Date(formData.check_in);
      const checkOut = new Date(formData.check_out);
      const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      if (nights > 0) {
        // Check each night the guest will sleep (check-in through night before checkout)
        let current = new Date(checkIn);
        while (current < checkOut) {
          const dateStr = current.toISOString().split('T')[0];
          if (blockedDates.includes(dateStr)) {
            setDateError("Some dates in your selection are not available");
            setNumNights(0);
            setTotalPrice(0);
            return;
          }
          current.setDate(current.getDate() + 1);
        }
        setNumNights(nights);
        setTotalPrice(nights * villa.price_per_night);
      } else {
        setNumNights(0);
        setTotalPrice(0);
      }
    }
  }, [formData.check_in, formData.check_out, villa, blockedDates]);

  const validateCoupon = async () => {
    if (!formData.coupon_code.trim()) { setCouponError("Please enter a coupon code"); return; }
    if (numNights < 1) { setCouponError("Please select dates first"); return; }
    setIsValidatingCoupon(true); setCouponError(""); setCouponData(null);
    try {
      const response = await axios.post(`${API}/validate-coupon`, {
        code: formData.coupon_code, villa_id: villa.id,
        check_in: formData.check_in, check_out: formData.check_out, total_amount: totalPrice
      });
      setCouponData(response.data);
      toast.success(`Coupon applied! You save $${response.data.discount_amount.toFixed(2)}`);
    } catch (error) {
      setCouponError(error.response?.data?.detail || "Invalid coupon code");
    } finally { setIsValidatingCoupon(false); }
  };

  const removeCoupon = () => { setCouponData(null); setCouponError(""); setFormData({...formData, coupon_code: ""}); };

  const generatorFee = villa?.generator_fee || 20;
  const bbqFee = villa?.bbq_fee || 15;
  const generatorTotal = formData.include_generator ? generatorFee * numNights : 0;
  const bbqTotal = formData.include_bbq ? bbqFee : 0;
  const addOnTotal = generatorTotal + bbqTotal;

  const getFinalPrice = () => {
    const base = couponData ? couponData.final_amount : totalPrice;
    return base + addOnTotal;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (numNights < 1 || dateError) { toast.error("Please select valid available dates"); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/reservations/create`, {
        villa_id: villa.id, ...formData,
        coupon_code: couponData ? formData.coupon_code : null,
        total_amount: getFinalPrice(), num_nights: numNights,
        generator_fee_total: generatorTotal,
        bbq_fee_total: bbqTotal
      });
      toast.success("Reservation request submitted! We'll contact you shortly.");
      onClose();
      setFormData({ guest_name: "", guest_email: "", guest_phone: "", check_in: "", check_out: "", num_guests: 2, special_requests: "", coupon_code: "", marketing_opt_in: false, include_generator: true, include_bbq: false });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setIsSubmitting(false); }
  };

  const nextImage = () => { if (villa?.images) setCurrentImageIndex((prev) => (prev + 1) % villa.images.length); };
  const prevImage = () => { if (villa?.images) setCurrentImageIndex((prev) => (prev - 1 + villa.images.length) % villa.images.length); };

  if (!villa) return null;

  const bathDisplay = Number.isInteger(villa.bathrooms) ? villa.bathrooms : villa.bathrooms?.toFixed(1);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl w-[98vw] sm:w-[95vw] max-h-[100dvh] sm:max-h-[95vh] overflow-hidden p-0 gap-0 rounded-t-2xl sm:rounded-2xl">
        {/* Mobile: Full-screen scroll. Desktop: Two columns */}
        <div className="flex flex-col lg:grid lg:grid-cols-[1fr,1fr] h-[100dvh] sm:h-auto sm:max-h-[95vh]">
          
          {/* Image Gallery */}
          <div className="relative h-[220px] sm:h-[320px] lg:h-full min-h-[220px] lg:min-h-[700px] bg-muted flex-shrink-0">
            <img 
              src={villa.images?.[currentImageIndex] || villa.image} 
              alt={`${villa.name} - Luxury vacation rental in Juan Dolio, Dominican Republic`}
              className="w-full h-full object-cover" 
              loading="eager"
            />
            {/* Close button - visible on mobile */}
            <button 
              onClick={() => onClose(false)} 
              className="absolute top-3 left-3 lg:hidden p-2 bg-black/50 hover:bg-black/70 rounded-full text-white z-20 transition-colors"
              aria-label="Close"
              data-testid="modal-close-mobile"
            >
              <X size={18} />
            </button>
            
            <div className="absolute top-3 right-3 flex gap-2 z-10">
              <button onClick={() => window.open(villa.images?.[currentImageIndex] || villa.image, '_blank')} className="p-2 bg-black/40 hover:bg-black/60 rounded-full shadow-md transition-colors" aria-label="View full size image">
                <ZoomIn size={16} className="text-white" />
              </button>
            </div>
            
            {villa.images?.length > 1 && (
              <>
                <button onClick={prevImage} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 sm:p-2 rounded-full shadow-lg" data-testid="prev-image-btn" aria-label="Previous image"><ChevronLeft size={18} /></button>
                <button onClick={nextImage} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 sm:p-2 rounded-full shadow-lg" data-testid="next-image-btn" aria-label="Next image"><ChevronRight size={18} /></button>
                
                {/* Thumbnail strip - hidden on very small mobile */}
                <div className="absolute bottom-2 left-2 right-2">
                  <div className="flex gap-1 justify-center overflow-x-auto py-1 px-2 bg-black/30 backdrop-blur-sm rounded-lg scrollbar-hide">
                    {villa.images.slice(0, 6).map((img, idx) => (
                      <button key={idx} onClick={() => setCurrentImageIndex(idx)} className={`flex-shrink-0 w-9 h-7 sm:w-14 sm:h-10 rounded overflow-hidden border-2 transition-all ${idx === currentImageIndex ? "border-white" : "border-transparent opacity-60"}`}>
                        <img src={img} alt={`${villa.name} photo ${idx + 1}`} className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                    {villa.images.length > 6 && (
                      <span className="flex-shrink-0 w-9 h-7 sm:w-14 sm:h-10 rounded bg-black/60 flex items-center justify-center text-white text-[10px] font-bold">+{villa.images.length - 6}</span>
                    )}
                  </div>
                </div>
              </>
            )}
            
            {villa.featured && <Badge className="absolute top-3 left-3 lg:left-3 bg-accent text-white border-0 text-xs px-2 py-0.5">Most Popular</Badge>}
            {villa.images?.length > 1 && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-black/50 text-white px-2 py-0.5 rounded-full text-xs">{currentImageIndex + 1}/{villa.images.length}</div>
            )}
          </div>

          {/* Booking Form - Scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <div className="p-4 sm:p-6 lg:p-8">
              {/* Villa Info Header */}
              <DialogHeader className="mb-3 sm:mb-4">
                <DialogTitle className="font-serif text-lg sm:text-2xl lg:text-3xl leading-tight">{villa.name}</DialogTitle>
                <p className="text-muted-foreground text-sm">{villa.tagline}</p>
              </DialogHeader>

              {/* Stats Row */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><Bed size={14} />{villa.bedrooms} BR</span>
                <span className="flex items-center gap-1"><Bath size={14} />{bathDisplay} BA</span>
                <span className="flex items-center gap-1"><Users size={14} />Up to {villa.guests}</span>
                <span className="flex items-center gap-1 star-rating"><Star size={14} fill="currentColor" /><span className="text-foreground font-semibold">{villa.rating}</span></span>
              </div>

              {/* Price per night */}
              <div className="flex items-baseline gap-1 mb-4 pb-3 border-b">
                <span className="font-serif text-2xl font-bold text-foreground">${villa.price_per_night}</span>
                <span className="text-muted-foreground text-sm">/ night</span>
              </div>

              {/* Airbnb-style Calendar */}
              <div className="mb-4">
                <BookingCalendar
                  blockedDates={blockedDates}
                  checkIn={formData.check_in}
                  checkOut={formData.check_out}
                  onDateSelect={handleDateSelect}
                  selectionMode={selectionMode}
                  pricePerNight={villa.price_per_night}
                />
              </div>

              {dateError && (
                <div className="flex items-center gap-2 text-red-600 text-sm p-3 bg-red-50 rounded-lg mb-3"><AlertCircle size={14} />{dateError}</div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3" data-testid="booking-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="guest_name" className="text-xs font-medium">Full Name</Label>
                    <Input id="guest_name" value={formData.guest_name} onChange={(e) => setFormData({...formData, guest_name: e.target.value})} placeholder="John Doe" required className="mt-1 h-10" data-testid="booking-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="guest_phone" className="text-xs font-medium">Phone</Label>
                    <Input id="guest_phone" type="tel" value={formData.guest_phone} onChange={(e) => setFormData({...formData, guest_phone: e.target.value})} placeholder="+1 234 567 8900" required className="mt-1 h-10" data-testid="booking-phone-input" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="guest_email" className="text-xs font-medium">Email</Label>
                  <Input id="guest_email" type="email" value={formData.guest_email} onChange={(e) => setFormData({...formData, guest_email: e.target.value})} placeholder="john@example.com" required className="mt-1 h-10" data-testid="booking-email-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="num_guests" className="text-xs font-medium">Guests</Label>
                    <Input id="num_guests" type="number" min="1" max={villa.guests} value={formData.num_guests} onChange={(e) => setFormData({...formData, num_guests: parseInt(e.target.value)})} required className="mt-1 h-10" data-testid="booking-guests-input" />
                  </div>
                  <div>
                    <Label htmlFor="special_requests" className="text-xs font-medium">Requests</Label>
                    <Input id="special_requests" value={formData.special_requests} onChange={(e) => setFormData({...formData, special_requests: e.target.value})} placeholder="Optional..." className="mt-1 h-10" data-testid="booking-requests-input" />
                  </div>
                </div>

                {/* Add-on Services */}
                {numNights > 0 && !dateError && (
                  <div className="space-y-3">
                    <div className="rounded-lg border bg-amber-50/60 p-3 space-y-2">
                      <p className="text-xs font-semibold text-foreground">Additional Services</p>
                      
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={formData.include_generator} onChange={(e) => setFormData({...formData, include_generator: e.target.checked})} className="mt-0.5" data-testid="generator-checkbox" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-1"><Zap size={12} className="text-amber-600" />24/7 Generator</span>
                            <span className="text-xs font-semibold text-primary">${generatorFee}/night</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground">Uninterrupted power for AC, lights & appliances</p>
                        </div>
                      </label>
                      
                      <label className="flex items-start gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={formData.include_bbq} onChange={(e) => setFormData({...formData, include_bbq: e.target.checked})} className="mt-0.5" data-testid="bbq-checkbox" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium flex items-center gap-1"><Flame size={12} className="text-orange-500" />BBQ Gas</span>
                            <span className="text-xs font-semibold text-primary">${bbqFee} flat</span>
                          </div>
                          <p className="text-[11px] text-muted-foreground">One-time fee for the entire stay</p>
                        </div>
                      </label>
                    </div>

                    {/* Coupon */}
                    <div className="flex gap-2">
                      <Input value={formData.coupon_code} onChange={(e) => { setFormData({...formData, coupon_code: e.target.value.toUpperCase()}); setCouponError(""); }} placeholder="Discount code" className="font-mono uppercase h-9 text-sm" disabled={!!couponData} data-testid="coupon-input" />
                      {couponData ? (
                        <Button type="button" variant="outline" size="sm" onClick={removeCoupon} data-testid="remove-coupon-btn"><X size={14} /></Button>
                      ) : (
                        <Button type="button" variant="secondary" size="sm" onClick={validateCoupon} disabled={isValidatingCoupon} data-testid="apply-coupon-btn">
                          {isValidatingCoupon ? <Loader2 className="animate-spin" size={14} /> : "Apply"}
                        </Button>
                      )}
                    </div>
                    {couponError && <p className="text-red-500 text-xs">{couponError}</p>}
                    {couponData && (
                      <div className="flex items-center gap-1.5 text-green-600 text-xs"><CheckCircle size={12} />{couponData.coupon_code} — {couponData.discount_type === "percentage" ? `${couponData.discount_value}% off` : `$${couponData.discount_value} off`}</div>
                    )}

                    {/* Price Summary */}
                    <div className="rounded-lg bg-muted/50 p-3 space-y-1.5 text-sm" data-testid="price-summary">
                      <div className="flex justify-between"><span className="text-muted-foreground">${villa.price_per_night} x {numNights} nights</span><span>${totalPrice.toFixed(2)}</span></div>
                      {formData.include_generator && <div className="flex justify-between"><span className="text-muted-foreground">Generator x {numNights}</span><span>${generatorTotal.toFixed(2)}</span></div>}
                      {formData.include_bbq && <div className="flex justify-between"><span className="text-muted-foreground">BBQ Gas</span><span>${bbqTotal.toFixed(2)}</span></div>}
                      {couponData && <div className="flex justify-between text-green-600"><span>Discount</span><span>-${couponData.discount_amount.toFixed(2)}</span></div>}
                      <Separator className="my-1" />
                      <div className="flex justify-between font-bold text-base"><span>Total</span><span className="text-primary">${getFinalPrice().toFixed(2)}</span></div>
                    </div>
                  </div>
                )}

                {/* Marketing opt-in */}
                <label className="flex items-start gap-2 cursor-pointer text-xs text-muted-foreground">
                  <input type="checkbox" checked={formData.marketing_opt_in} onChange={(e) => setFormData({...formData, marketing_opt_in: e.target.checked})} className="mt-0.5" data-testid="marketing-opt-in" />
                  Send me exclusive deals and travel tips
                </label>

                {/* Submit - sticky on mobile */}
                <div className="sticky bottom-0 bg-background pt-2 pb-1 -mx-4 px-4 sm:static sm:mx-0 sm:px-0 sm:pt-0 sm:pb-0">
                  <Button 
                    type="submit" 
                    className="w-full btn-accent py-5 text-base font-semibold"
                    disabled={isSubmitting || numNights < 1 || !!dateError}
                    data-testid="booking-submit-btn"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Submitting...</>
                    ) : numNights > 0 ? (
                      `Request Reservation — $${getFinalPrice().toFixed(2)}`
                    ) : (
                      'Select dates to book'
                    )}
                  </Button>
                  <p className="text-[10px] text-center text-muted-foreground mt-1.5">
                    We'll contact you via WhatsApp or email to confirm
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
